import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { ResponsivePie } from '@nivo/pie'
import React, { useEffect } from 'react'
import { formatBalance } from 'src/common/helpers'
import { ChainName } from 'src/common/static'
import { useAuth, useBlockchain } from 'src/common/context'
import { theme } from 'src/ui'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { QCADIcon } from '../../../common/assets/QCADIcon'
interface WelcomeCardProps {
  firstName?: string
}

const WelcomeCard: React.FC<WelcomeCardProps> = ({ firstName }: WelcomeCardProps) => {
  const { wallets, fetchBlockchainDetails } = useBlockchain()
  const { socket } = useAuth()

  // Calculate QCAD distribution by chain
  const qcadByChain = wallets.reduce(
    (acc, wallet) => {
      const { chain, balance } = wallet
      if (chain === ChainName.ETHEREUM) {
        return { ...acc, ethereum: acc.ethereum + (balance?.qcad ?? 0) }
      }
      if (chain === ChainName.BASE) {
        return { ...acc, base: acc.base + (balance?.qcad ?? 0) }
      }
      return { ...acc }
    },
    { ethereum: 0, base: 0 }
  )

  const totalQCAD = qcadByChain.ethereum + qcadByChain.base

  const data = [
    {
      id: 'Ethereum',
      label: 'Ethereum',
      value: qcadByChain.ethereum,
      color: theme.palette.primary[98]
    },
    {
      id: 'Base',
      label: 'Base',
      value: qcadByChain.base,
      color: theme.palette.primary[95]
    }
  ].filter((item) => item.value > 0)

  useEffect(() => {
    fetchBlockchainDetails()
    if (!socket) return

    const handler = async () => {
      console.debug('Received balances socket update')
      await fetchBlockchainDetails()
    }

    socket.on('balances', handler)

    return () => {
      socket.off('balances', handler)
    }
  }, [socket])

  return (
    <Box display="flex" alignItems="stretch" justifyContent="space-between" width="100%" height="100%">
      <Stack spacing={3} flex="1 1 50%">
        <Typography variant="h5" fontWeight={500}>
          Welcome back, {firstName || 'User'}!
        </Typography>
        <Box>
          <Box display="flex" alignItems="center" gap={0.5} mb={1}>
            <Typography variant="body2" color="text.secondary">
              Current QCAD Balance
            </Typography>

            <Tooltip title="Refresh the page to update your latest balance. This balance reflects the total QCAD across both the Minting and Treasury wallets.">
              <IconButton size="small" sx={{ padding: 0 }}>
                <InfoOutlinedIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <QCADIcon size={24} />
            <Typography variant="h4" fontWeight={600}>
              ${formatBalance(totalQCAD)}
            </Typography>
          </Box>
        </Box>
      </Stack>
      <Box
        height={200}
        width={200}
        position="relative"
        flex="1 1 50%"
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
      >
        {data.length > 0 && (
          <Box
            position="relative"
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <ResponsivePie
              data={data}
              valueFormat={(value) => value.toString()}
              arcLabel="none"
              margin={{ top: 10, bottom: 10, left: 10, right: 120 }}
              innerRadius={0.5}
              padAngle={2}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              colors={{ datum: 'data.color' }}
              enableArcLinkLabels={false}
              startAngle={-90}
              motionConfig="default"
            />
          </Box>
        )}
        <Box position="absolute" right={0} top="50%" sx={{ transform: 'translateY(-50%)' }}>
          <Stack spacing={1}>
            {data.map((item) => (
              <Box key={item.id} display="flex" alignItems="center" gap={1}>
                <Box width={12} height={12} borderRadius="50%" sx={{ backgroundColor: item.color }} />
                <Typography variant="body2" fontSize="0.875rem">
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}

export default WelcomeCard
